import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Book, BookOpen, User, Calendar, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

const MyCoursesPage = () => {
  const { user } = useContext(AuthContext);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSemester, setCurrentSemester] = useState('All');
  const [semesters, setSemesters] = useState(['All']);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${user.id}/subscriptions`, 
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch subscriptions');
        }

        const data = await response.json();
        setSubscriptions(data);

        // Extract unique semesters for filtering
        const uniqueSemesters = ['All', ...new Set(data.map(sub => `${sub.semester} ${sub.year}`))];
        setSemesters(uniqueSemesters);
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        toast.error('Failed to load your courses');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchSubscriptions();
    }
  }, [user?.id]);

  const handleUnsubscribe = async (subscriptionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/subscriptions/${subscriptionId}`, 
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to unsubscribe');
      }

      // Remove the unsubscribed course from state
      setSubscriptions(subscriptions.filter(sub => sub.id !== subscriptionId));
      toast.success('Successfully unsubscribed from course');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to unsubscribe from course');
    }
  };

  // Filter subscriptions by selected semester
  const filteredSubscriptions = currentSemester === 'All'
    ? subscriptions
    : subscriptions.filter(sub => `${sub.semester} ${sub.year}` === currentSemester);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Courses</h1>
        <Link 
          to="/courses" 
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          <Plus size={18} className="mr-2" />
          Browse Courses
        </Link>
      </div>

      {/* Semester filter */}
      <div className="mb-6">
        <label htmlFor="semester" className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Filter by Semester:
        </label>
        <select
          id="semester"
          value={currentSemester}
          onChange={(e) => setCurrentSemester(e.target.value)}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-red-500 focus:border-red-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          {semesters.map((semester) => (
            <option key={semester} value={semester}>
              {semester}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      ) : filteredSubscriptions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSubscriptions.map((subscription) => (
            <div 
              key={subscription.id} 
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                      {subscription.courseCode}: {subscription.courseName}
                    </h2>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <BookOpen size={16} className="mr-1" />
                      <span className="mr-2">{subscription.departmentCode}</span>
                      <Calendar size={16} className="mr-1" />
                      <span>{subscription.semester} {subscription.year}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center mb-4 text-sm text-gray-700 dark:text-gray-300">
                  <User size={16} className="mr-2" />
                  <span>Prof. {subscription.professorName}</span>
                </div>

                <div className="flex mt-4 justify-between">
                  <Link
                    to={`/courses/${subscription.courseId}/sections/${subscription.sectionId}`}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    <Book size={16} className="mr-2" />
                    View Materials
                  </Link>
                  
                  <button
                    onClick={() => handleUnsubscribe(subscription.id)}
                    className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 text-sm underline"
                  >
                    Unsubscribe
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <Book size={48} className="mx-auto text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No Courses Found</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            {currentSemester !== 'All' 
              ? `You haven't subscribed to any courses for ${currentSemester}.` 
              : "You haven't subscribed to any courses yet."}
          </p>
          <Link
            to="/courses"
            className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <Plus size={18} className="mr-2" />
            Browse Courses
          </Link>
        </div>
      )}
    </div>
  );
};

export default MyCoursesPage;